/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Game domain - Core game loop orchestration and state management
 * game.client.ts - Client-side game loop orchestration, rendering coordination, and state management
 */

import { WORLD } from "@game/config";
import { gameActions } from "@game/state";

export interface ClientGameLoopState {
  isRunning: boolean;
  lastFrameTime: number;
  frameCount: number;
  animationId?: number;
}

export class ClientGameOrchestrator {
  private loopState: ClientGameLoopState = {
    isRunning: false,
    lastFrameTime: 0,
    frameCount: 0,
    animationId: undefined,
  };

  private updateFunctions: {
    interpolatePlayers?: () => void;
    interpolateAsteroids?: () => void;
    interpolatePellets?: () => void;
    updateBulletPositions?: () => void;
    updateCamera?: () => void;
    updateMouseInput?: () => void;
    updateFPS?: () => void;
    render?: () => void;
    updateParticles?: (deltaTime: number) => void;
    updateThrustParticles?: (deltaTime: number) => void;
    spawnThrustParticles?: () => void;
  } = {};

  private getGameState?: () => any;

  initialize(
    updateFunctions: typeof this.updateFunctions,
    getGameState: () => any,
  ) {
    this.updateFunctions = updateFunctions;
    this.getGameState = getGameState;
  }

  private gameLoop = () => {
    const now = performance.now();
    const deltaTime = this.loopState.lastFrameTime
      ? (now - this.loopState.lastFrameTime) / 1000
      : 0;
    this.loopState.lastFrameTime = now;
    this.loopState.frameCount++;

    const state = this.getGameState?.();
    if (!state) {
      console.warn("Game state not available");
      if (this.loopState.isRunning) {
        this.loopState.animationId = requestAnimationFrame(this.gameLoop);
      }
      return;
    }

    if (state.started) {
      this.updateFunctions.interpolatePlayers?.();
      this.updateFunctions.interpolateAsteroids?.();
      this.updateFunctions.interpolatePellets?.();
      this.updateFunctions.updateBulletPositions?.();

      gameActions.runInterpolation(performance.now(), WORLD);

      this.updateFunctions.updateCamera?.();
      this.updateFunctions.updateMouseInput?.();
      this.updateFunctions.updateFPS?.();
    }

    this.updateFunctions.updateParticles?.(deltaTime);
    this.updateFunctions.updateThrustParticles?.(deltaTime);
    this.updateFunctions.spawnThrustParticles?.();

    this.updateFunctions.render?.();

    if (this.loopState.isRunning) {
      this.loopState.animationId = requestAnimationFrame(this.gameLoop);
    }
  };

  start() {
    if (this.loopState.isRunning) {
      console.warn("Client game loop is already running");
      return;
    }

    console.log("Starting client game loop");
    this.loopState.isRunning = true;
    this.loopState.lastFrameTime = performance.now();
    this.loopState.frameCount = 0;
    this.loopState.animationId = requestAnimationFrame(this.gameLoop);
  }

  stop() {
    if (!this.loopState.isRunning) {
      console.warn("Client game loop is not running");
      return;
    }

    console.log("Stopping client game loop");
    this.loopState.isRunning = false;

    if (this.loopState.animationId) {
      cancelAnimationFrame(this.loopState.animationId);
      this.loopState.animationId = undefined;
    }
  }

  getStatus() {
    return {
      isRunning: this.loopState.isRunning,
      frameCount: this.loopState.frameCount,
      lastFrameTime: this.loopState.lastFrameTime,
    };
  }

  getFPS() {
    const state = this.getGameState?.();
    return state?.fps || 0;
  }
}

export const clientGameOrchestrator = new ClientGameOrchestrator();

export function toggleVanillaUI(show: boolean) {
  const vanillaElements = ["joinModal", "deathModal", "toasts", "leaderboard"];

  vanillaElements.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.style.display = show ? "" : "none";
    }
  });
}

export function isReactReady() {
  return window.React && window.ReactDOM && window.uiBridge;
}

export function initializeReactIntegration(delay = 1000) {
  setTimeout(() => {
    if (isReactReady()) {
      console.log("React UI available but keeping vanilla UI as fallback");
      // Don't hide vanilla UI - React isn't working properly yet
    } else {
      console.log("React UI not available, using vanilla UI");
    }
  }, delay);
}
